import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { useCountry } from './../CountryContext';
import { Link } from "react-router-dom";

const Dropdown = ({ type, value, options, onSelect, countryValue }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownMenuRef = useRef(null);
  const contentRef = useRef(null);
  const thumbRef = useRef(null);
  const [thumbHeight, setThumbHeight] = useState(20);
  const [scrollTop, setScrollTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedItems, setSelectedItems] = useState({});

  // Define all possible options
  const allOptions = {
     
      CROPS: [
          { id: "african-night-shade", name: "African Night Shade", value: "african-night-shade" },
          { id: "alovera", name: "Alovera", value: "alovera" },
          { id: "avocados", name: "Avocados", value: "avocados" },
          { id: "apple", name: "Apple", value: "apple" },
          { id: "bananas", name: "Bananas", value: "bananas" },
          { id: "baobab", name: "Baobab", value: "baobab" },
          { id: "beans", name: "Beans", value: "beans" },
          { id: "beetroot", name: "Beetroot", value: "beetroot" },
          { id: "black-nightshade", name: "Black Nightshade", value: "black-nightshade" },
          { id: "cabbage", name: "Cabbage", value: "cabbage" },
          { id: "capsicum", name: "Capsicum", value: "capsicum" }
      ],
      LIVESTOCK: [
          { id: "bees", name: "Bees", value: "bees" },
          { id: "cattle-dairy", name: "Cattle - Dairy", value: "cattle-dairy" },
          { id: "fish", name: "Fish", value: "fish" },
          { id: "catfish", name: "Catfish", value: "catfish" }
      ],
      FOREST: [
          { id: "bamboo", name: "Bamboo", value: "bamboo" },
          { id: "blue-gum", name: "Blue Gum", value: "blue-gum" }
      ]
  };

  // Get the correct options based on dropdown type
  const dropdownOptions = allOptions[type] || options || [];

  const capitalizeWords = (str) => {
      if (!str) return "";
      return str
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(" ");
  };

  const getDisplayValue = () => {
      if (value) {
          if (type === "COUNTRY") return value.name || "-";
          return value.name || value.value || "-";
      }
      return "-";
  };

  const getHeaderTitle = () => {
      switch (type) {
          case "COUNTRY":
              return "Countries";
          case "REGION":
              return "Regions";
          case "DISTRICT":
              return "Districts";
          case "MUNICIPALITY":
              return "Municipalities";
          case "SMMEs":
              return "SMMEs";
          case "VALUE CHAIN":
              return "Value Chains";
          case "PROJECT":
              return "Projects";
          case "USER TYPE":
              return "User Types";
          case "SERVICES":
              return "Services";
          case "CROPS":
              return "Crops";
          case "LIVESTOCK":
              return "Livestock";
          case "FOREST":
              return "Forest";
          default:
              return "Select Option";
      }
  };

  const toggleItemSelection = (itemId) => {
      setSelectedItems(prev => ({
          ...prev,
          [itemId]: !prev[itemId]
      }));
  };

  const renderRegularItems = () => {
      return dropdownOptions?.map((option, index) => (
          <li
              key={index}
              className={`group flex items-center justify-between px-3 py-2 text-sm cursor-pointer . transition-colors duration-200 rounded ${
                  selectedItems[option.id] ? "bg-[#2E453D]" : ""
              }`}
          >
              <span 
                  className="text-white"
                  onClick={(e) => {
                      e.stopPropagation();
                      onSelect(option);
                      setIsOpen(false);
                  }}
              >
                  {option.name || option.value}
              </span>
              <button 
                  onClick={(e) => {
                      e.stopPropagation();
                      toggleItemSelection(option.id);
                  }}
              >
                  {selectedItems[option.id] ? <SelectedIcon /> : <UnselectedIcon />}
              </button>
          </li>
      ));
  };

  const renderGroupedItems = () => {
    return (
      <div className="mb-2">
        {/* Always show all sections */}
        <h4 className="text-white font-bold px-3 py-1 .">CROPS:</h4>
        <ul>
          {allOptions.CROPS.map((item, index) => (
            <li
              key={`crop-${index}`}
              className={`group flex items-center justify-between px-3 py-2 text-sm text-white cursor-pointer . transition-colors duration-200 rounded ${
                selectedItems[item.id] ? "" : ""
              }`}
            >
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(item);
                  setIsOpen(false);
                }}
              >
                {item.name}
              </span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItemSelection(item.id);
                }}
              >
                {selectedItems[item.id] ? <SelectedIcon /> : <UnselectedIcon />}
              </button>
            </li>
          ))}
        </ul>
  
        <h4 className="text-white font-bold px-3 py-1 . mt-2">LIVESTOCK:</h4>
        <ul>
          {allOptions.LIVESTOCK.map((item, index) => (
            <li
              key={`livestock-${index}`}
              className={`group flex items-center justify-between px-3 py-2 text-sm text-white cursor-pointer . transition-colors duration-200 rounded ${
                selectedItems[item.id] ? "" : ""
              }`}
            >
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(item);
                  setIsOpen(false);
                }}
              >
                {item.name}
              </span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItemSelection(item.id);
                }}
              >
                {selectedItems[item.id] ? <SelectedIcon /> : <UnselectedIcon />}
              </button>
            </li>
          ))}
        </ul>
  
        <h4 className="text-white font-bold px-3 py-1 . mt-2">FOREST:</h4>
        <ul>
          {allOptions.FOREST.map((item, index) => (
            <li
              key={`forest-${index}`}
              className={`group flex items-center justify-between px-3 py-2 text-sm text-white cursor-pointer transition-colors duration-200 rounded ${
                selectedItems[item.id] ? "" : ""
              }`}
            >
              <span 
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(item);
                  setIsOpen(false);
                }}
              >
                {item.name}
              </span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItemSelection(item.id);
                }}
              >
                {selectedItems[item.id] ? <SelectedIcon /> : <UnselectedIcon />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const UnselectedIcon = () => (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" stroke="none"/>
    </svg>
  );
  
  const SelectedIcon = () => (
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8.5" cy="8.5" r="8" fill="#2E453D" stroke="#FFBD26"/>
      <path d="M5 8.5L7.5 11L12 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const handleScroll = () => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    setScrollTop(scrollTop);
    
    const thumbH = Math.max(
      (clientHeight / scrollHeight) * clientHeight,
      20
    );
    setThumbHeight(thumbH);
  };

  const handleTrackClick = (e) => {
    if (!contentRef.current || !thumbRef.current) return;
    const trackRect = e.currentTarget.getBoundingClientRect();
    const thumbOffset = e.clientY - trackRect.top - thumbHeight / 2;
    const scrollPercentage = thumbOffset / (trackRect.height - thumbHeight);
    contentRef.current.scrollTop =
      scrollPercentage * (contentRef.current.scrollHeight - contentRef.current.clientHeight);
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    
    const startY = e.clientY;
    const startScrollTop = scrollTop;
    
    const handleMouseMove = (e) => {
      if (!isDragging || !contentRef.current) return;
      const deltaY = e.clientY - startY;
      const trackHeight = contentRef.current.clientHeight - thumbHeight;
      const scrollDelta = (deltaY / trackHeight) * contentRef.current.scrollHeight;
      contentRef.current.scrollTop = startScrollTop + scrollDelta;
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownMenuRef.current && !dropdownMenuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      handleScroll();
    }
  }, [isOpen, options]);

  return (
      <div className="relative md:mr-4 mr-2" ref={dropdownMenuRef}>
      <button
        className={`relative xl:w-[205px] lg:w-[160px] md:w-[100px] w-[70px] h-[20px] md:h-[53px] bg-[#2E453D] text-white flex justify-between items-center text-[8px] md:text-[17px] px-[2px] rounded-[4px] truncate`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`whitespace-normal break-words font-normal leading-[0.90] text-center capitalize w-full ${
          getDisplayValue() === "-" ? "text-gray-400" : ""
        }`}>
          {capitalizeWords(getDisplayValue())}
        </span>
        
        <span className="absolute bottom-[1px] right-3 text-[6px] md:text-[8px] text-white">
          {(type === "COUNTRY" && value?.code) || 
           (type === "REGION" && value?.value) || 
           (type === "DISTRICT" && value?.value) || 
           (type === "SMMEs" && value?.value) || 
           '-'}
        </span>
        
        <img
          src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751799445/Expand_down_light_syoynq.png"
          alt="expand"
          className="absolute bottom-2 right-2 w-2 h-2 md:w-5 md:h-5"
        />
      </button>

   
      <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="bg-[#2E453D]/80 rounded-b-[5px] overflow-x-auto backdrop-blur-sm absolute top-full mt-1 left-0 w-full z-50 md:w-[205px]"
                    >
                        <div className="px-3 py-2">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-white text-sm font-semibold">
                                    {getHeaderTitle()}
                                </h3>
                                <img 
                                    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1750628487/Export_light_kuxwb6.png" 
                                    alt="Export icon"
                                    className="w-4 h-4 ml-2 cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsOpen(false);
                                    }}
                                />
                            </div>
                            <div className="custom-scroll-area max-h-[200px]">
                                <div
                                    className="custom-scroll-content"
                                    ref={contentRef}
                                    onScroll={handleScroll}
                                >
                                    <ul className="space-y-2">
                                        {type === "CROPS" || type === "LIVESTOCK" || type === "FOREST"
                                            ? renderGroupedItems()
                                            : renderRegularItems()}
                                    </ul>
                                </div>
                                {/* ... scroll track code ... */}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
    </div>
  );
};

const farmers = [
  {
    name: "African Marmalade",
    category: "Fruits & Vegetables",
    location: "Nyika, Country",
    rating: "4.3 (653)",
    link: "/Contract2",
    farmersCount: "12,001",
    image: "https://res.cloudinary.com/dzngpgki4/image/upload/v1752266477/image_1846_yeduna.png",
    available: true
  },
  {
    name: "Farmarket Limited",
    category: "Fruits & Vegetables",
    location: "Aloth Market",
    rating: "4.1 (610)",
    link: "/cab2",
    farmersCount: "8,412",
    image: "https://res.cloudinary.com/dzngpgki4/image/upload/v1752266476/image_1844_qjhe0y.png",
    available: false
  },
  {
    name: "Cabbages",
    category: "Fruits & Vegetables",
    location: "Nyika, Country",
    rating: "4.3 (603)",
    link: "/cab3",
    farmersCount: "10,130",
    image: "https://res.cloudinary.com/dzngpgki4/image/upload/v1752266476/image_1843_t32aes.png",
    available: false
  },
  {
    name: "Cabbages",
    category: "Fruits & Vegetables",
    location: "Nyika, Country",
    rating: "4.0 (599)",
    link: "/cab4",
    farmersCount: "6,928",
    image: "https://res.cloudinary.com/dzngpgki4/image/upload/v1752266476/image_1845_lfqruq.png",
    available: false
  },
];
  const Contract1th = () => {
    // In your component, add state for the popup
const [showPopup, setShowPopup] = useState(false);
const [popupMessage, setPopupMessage] = useState("");
const handleInProgressClick = (pageName) => {
  setPopupMessage("Contract currently not available");
  setShowPopup(true);
  setTimeout(() => setShowPopup(false), 3000); // Auto-close after 3 seconds
};

      const [agreed, setAgreed] = useState(false);
    const renderButtonWithIcon = (text, bgColor, iconColor, isLarge = false) => {
      const bgColorClasses = {
        "#152C24": "bg-[#152C24]",
        "#2E453D": "bg-[#2E453D]",
        "#FDBB26": "bg-[#FDBB26]",
      };
  
      const iconColorClasses = {
        "#152C24": "bg-[#152C24]",
        "#2E453D": "bg-[#2E453D]",
        "#FDBB26": "bg-[#FDBB26]",
      };
  
      return (
        <div className="flex items-center">
          <div className={`${bgColorClasses[bgColor]} text-white md:text-[16px] text-[5px] px-2 md:px-4 ${
            isLarge ? "md:py-[14px] py-[3px]" : "md:py-[14px] py-[3px]"
          } md:rounded-l-md rounded-l-md font-bold md:w-[300px] whitespace-nowrap flex items-center h-full`}>
            {text}
          </div>
          
          <div className={`${iconColorClasses[iconColor]} hidden md:flex items-center md:py-2  justify-center md:w-[57px] md:h-[52px] rounded-r-md`}>
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744139834/Expand_right_2_a4wodr.png"
              alt="Expand"
              className="w-[20px] h-[6px] md:h-[40px] md:w-[55px]  object-contain"
            />
          </div>
          
          <div className={`${iconColorClasses[iconColor]} md:hidden flex items-center justify-center w-[20px]  h-[14px] rounded-r-md`}>
            <img
              src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744370865/Expand_right_5_l2cjmf.png"
              alt="Expand"
              className="w-[20px] h-[6px]  object-contain"
            />
          </div>
        </div>
      );
    };
  
    const { selectedCountry } = useCountry();
    const [selectedService, setSelectedService] = useState(null);
    const [reportSelections, setReportSelections] = useState({
        COUNTRY: "-",
        REGION: "-",
        DISTRICT: "-",
        MUNICIPALITY: "-",
        SMMEs: "-",
        "VALUE CHAIN": "-",
        PROJECT: "-",
        "USER TYPE": "-"
    });
  
    const dropdownOptions = [
        { id: 1, name: "Option 1", value: "opt1" },
        { id: 2, name: "Option 2", value: "opt2" },
        { id: 3, name: "Option 3", value: "opt3" },
    ];

    const handleDropdownSelect = (option) => {
        console.log("Selected:", option);
    };

    return (
        <div className='bg-white'>
            <div className='md:px-20 px-4 md:pt-6 pt-2 md:pb-5 pb-2'>
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0">
                    <h2 className="text-[14px] md:text-[36px] capitalize text-[#152C24] font-bold">
                        <span className="text-[#979797] capitalize">SERVICES | </span>{selectedCountry?.name || 'South Africa'}
                    </h2>
                    <div className="w-full md:w-auto">
                        <h1 className="text-[10px] md:text-[20px] font-extrabold md:mb-2">USER NUMBER</h1>
                        <div className="flex w-full md:w-fit">
                            <button
                                className={`w-full md:w-[250px] h-[25px] md:h-[53px] bg-[#152C24] text-white font-medium flex items-center px-2 rounded-l-[5px]`}>
                                <input className="text-[10px] md:text-[18px] md:px-2 bg-transparent outline-none placeholder-white w-full" placeholder="XXXXXXXXX"/>
                            </button>
                            <div
                                className="w-[25px] h-[25px] md:h-[53px] md:w-[50px] bg-[#EEB527] text-white flex justify-center items-center rounded-r-[5px] -ml-1 z-30 cursor-pointer"
                            >
                                <img
                                    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747251939/Expand_right_wbmjno.png"
                                    alt="expand"
                                    className="w-3 h-3 md:w-6 md:h-[24px]"
                                />
                            </div>
                        </div>
                        <p className="text-[8px] md:text-[14px] font-normal mt-1 text-black">
                            Insert ID Number
                        </p>
                    </div>
                </div>
                
                {/* Search Section */}
                <div className="flex flex-col md:flex-row items-center w-full md:mb-4 md:mt-6 my-3 gap-2 md:gap-12">
                <Dropdown 
    type="CROPS"  // Add this prop to specify which options to show
    value={selectedService} 
    onSelect={(option) => setSelectedService(option)}
/>
                    
                    <div className="flex-1 w-full relative">
                        <input
                            placeholder="Search"
                            type="text"
                            className="w-full h-[30px] md:h-[61px] text-[#979797] text-[10px] md:text-[16px] font-semibold p-2 pr-8 md:p-3 md:px-6 border border-[#C4C4C4] rounded-md focus:outline-none focus:ring-2 focus:ring-green-600"
                        />
                        <img
                            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744490602/Search_k7vxkr.png"
                            alt="Search Icon"
                            className="w-[14px] h-[14px] md:h-[24px] md:w-[24px] absolute right-3 top-1/2 transform -translate-y-1/2"
                        />
                    </div>
                </div>
                
                {/* Hero Banner */}
                <h2 className="text-[14px] md:text-[36px] capitalize text-[#152C24] font-bold">
                    Contracts
                </h2>
                
                <div className="relative w-full h-[150px] md:h-[650px] mt-2 md:mt-0">
                    <img
                        src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1752760167/image_1875_vbsf0r.png"
                        alt="Securing Agriculture in Namibia"
                        className="w-full h-full object-cover md:rounded-lg rounded-md transition-opacity duration-500"
                    />
                    
                    {/* Text and button container */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 md:px-12 md:pb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-1 md:gap-0">
                        {/* Left side text */}
                        <div className="text-white">
                            <h2 className="text-[14px] md:text-[40px] font-bold">Cabbages | Var. DH2</h2>
                            <p className="text-[10px] md:text-[32px] font-semibold">Buy new variety of high yield seed</p>
                        </div>
                        
                        {/* Right side button */}
                        <div className="text-right w-full md:w-auto">
                            {renderButtonWithIcon("Buy now", "#152C24", "#FDBB26", true)}
                        </div>
                    </div>
                </div>
                
                {/* Content Section */}
                <h2 className="text-[14px] md:text-[36px] md:mt-6 mt-3 capitalize text-[#152C24] font-bold">
                    <span className="text-[#979797] capitalize">Contracts </span>| Cabbage
                </h2>
                <p className='text-[10px] md:text-[32px] font-medium md:mb-10 mb-4'>
                    The FarmNet website serves as a digital platform for connecting smallholder and emerging farmers with institutional buyers, waste-to-farm initiatives, and sustainability-driven food systems. Through this platform, registered farmers can access offtake contracts with buyers like hospitals, retailers, and food service companies, all within a verified, traceable value chain.
                </p>
                
                {/* Farmers Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-10">
  {farmers.map((farm, index) => (
    <div 
      key={index} 
      className="w-full rounded-md bg-white hover:shadow-xl transition duration-300"
      onClick={() => {
        if (!farm.available) {
          handleInProgressClick(farm.name);
        }
      }}
    >
      <Link to={farm.available ? farm.link : "#"} className="block">
        <img
          src={farm.image}
          alt={farm.name}
          className="w-full h-[120px] md:h-[350px] object-cover rounded"
          style={{ borderRadius: "5px" }}
        />
        <div className="p-2 md:px-4 px-2 bg-[#E7EBEB]">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-[12px] md:text-[20px] truncate">{farm.name}</h2>
            <div className="flex items-center">
              <span className="font-semibold text-[10px] md:text-[20px] truncate">{farm.category}</span>
            </div>
          </div>

          <div className="flex justify-between text-black mt-1">
            <div className="text-black font-normal text-[10px] md:text-[20px]">
              Cabbages
            </div>
            <div className="flex items-center">
              <img 
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744892528/star_hmxstj.png" 
                alt="rating star" 
                className="w-[12px] h-[12px] md:w-[20px] md:h-[20px]" 
              />
              <span className="text-black font-normal text-[10px] md:text-[20px] ml-1">{farm.rating}</span>
            </div>
          </div>
        
        </div>
      </Link>
    </div>
  ))}
</div>
                <div className="flex justify-end md:mt-8 mt-4">
        <a href="/Contract2">
       <div>
            {renderButtonWithIcon("Contracts", "#2E453D", "#152C24", true)}
            <p className="md:text-[14px] text-[6px] font-normal mt-1">Click to go to contract</p>
          </div>
       </a>
        </div>
        
        <div className="flex justify-end md:mt-6 mt-3">
          <div className="flex gap-1 md:gap-2 items-center">
            <h1 className="md:text-[24px] text-[8px] font-normal">WhatsApp us</h1>
            <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1752160024/image_1785_bsrl4k.png" alt="link" className="w-[12px] h-[12px] md:w-[48px] md:h-[48px]"/>
          </div>
        </div>
        
        <div className="flex gap-4 items-center mt-5">
                          <div
                              className="md:w-16 md:h-[63px] w-6 h-[30px] rounded-[5px] bg-[#D9D9D9] flex items-center justify-center cursor-pointer transition"
                              onClick={() => {
                                  setAgreed((prev) => !prev);
                              }}
                              tabIndex={0}
                              aria-pressed={agreed}
                              aria-label="Confirm terms and conditions">
                              {/* Black or Green Check Round Fill SVG */}
                              {agreed ? (
                                  // GREEN CHECK ROUND FILL
                                  <svg
                                      width="54"
                                      height="54"
                                      viewBox="0 0 54 54"
                                      fill="none"
                                      className="md:w-[54px] md:h-[54px] w-[20px] h-[20px] transition-all duration-150"
                                      xmlns="http://www.w3.org/2000/svg">
                                      <circle
                                          cx="27"
                                          cy="27"
                                          r="27"
                                          fill="#152C24"
                                      />
                                      <path
                                          d="M39 21L24.75 35L15 25.4545"
                                          stroke="white"
                                          strokeWidth="4"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                      />
                                  </svg>
                              ) : (
                                  // BLACK CHECK ROUND FILL
                                  <svg
                                      width="54"
                                      height="54"
                                      viewBox="0 0 54 54"
                                      fill="none"
                                      className="md:w-[54px] md:h-[54px] w-[20px] h-[20px] transition-all duration-150"
                                      xmlns="http://www.w3.org/2000/svg">
                                      <circle
                                          cx="27"
                                          cy="27"
                                          r="27"
                                          fill="#222"
                                      />
                                      <path
                                          d="M39 21L24.75 35L15 25.4545"
                                          stroke="white"
                                          strokeWidth="4"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                      />
                                  </svg>
                              )}
                          </div>
                          <p className="md:text-[32px] text-[6px]">
                          About | Terms of Use | Privacy | Contact | © 2025 Farmers Networks
                          </p>

                   

                      {/* Text */}
                  </div>
            </div>

{showPopup && (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 20 }}
    className="fixed bottom-4 right-4 bg-[#2E453D] text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2 border border-[#FDBD26]"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6 text-[#FDBD26]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
    <span>{popupMessage}</span>
  </motion.div>
)}
        </div>
    );
}

export default Contract1th;
